import React, { useMemo, useState } from "react"
import { ComposableMap, Geographies, Geography } from "react-simple-maps"
import "./TerritoryMap.css"

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json"

const AVAILABLE_STATES = [
  { name: "Alabama", code: "AL" },
  { name: "Alaska", code: "AK" },
  { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" },
  { name: "Colorado", code: "CO" },
  { name: "Delaware", code: "DE" },
  { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" },
  { name: "Idaho", code: "ID" },
  { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" },
  { name: "Kentucky", code: "KY" },
  { name: "Louisiana", code: "LA" },
  { name: "Massachusetts", code: "MA" },
  { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" },
  { name: "Montana", code: "MT" },
  { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" },
  { name: "New Hampshire", code: "NH" },
  { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" },
  { name: "Ohio", code: "OH" },
  { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" },
  { name: "Pennsylvania", code: "PA" },
  { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" },
  { name: "Utah", code: "UT" },
  { name: "Vermont", code: "VT" },
  { name: "West Virginia", code: "WV" },
  { name: "Wyoming", code: "WY" },
]

const TerritoryMap = () => {
  const [selectedState, setSelectedState] = useState(null)

  const statesByName = useMemo(() => {
    const map = new Map()
    AVAILABLE_STATES.forEach((state) => {
      map.set(state.name, state)
    })
    return map
  }, [])

  const scrollToBooking = () => {
    const element = document.getElementById("booking-form")
    const headerOffset = 80
    const elementPosition = element ? element.offsetTop : 0
    const offsetPosition = elementPosition - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    })
  }

  return (
    <section className='territory-map section section-light-gray'>
      <div className='container'>
        <div className='territory-map__header animate-fade-up'>
          <h2 className='section-title'>Check Territory Availability</h2>
          <p className='section-subtitle'>
            Explore where Cordova franchises are open today. Tap an available
            state in green to confirm territory availability and take the next
            step toward ownership.
          </p>
        </div>

        <div className='territory-map__layout'>
          <div className='territory-map__map animate-fade-up'>
            <ComposableMap
              projection='geoAlbersUsa'
              projectionConfig={{ scale: 1000 }}
            >
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const stateName = geo.properties?.name
                    const stateData = statesByName.get(stateName)
                    const isAvailable = Boolean(stateData)
                    const isSelected =
                      selectedState && stateName === selectedState.name

                    const fill = !isAvailable
                      ? "#d9d9d9"
                      : isSelected
                      ? "#004d2a"
                      : "#006e3d"

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onClick={
                          isAvailable ? () => setSelectedState(stateData) : undefined
                        }
                        style={{
                          default: {
                            fill,
                            stroke: "#ffffff",
                            strokeWidth: 0.6,
                            outline: "none",
                            cursor: isAvailable ? "pointer" : "default",
                            transition: "fill 0.2s ease-out",
                          },
                          hover: {
                            fill: isAvailable ? "#00924c" : "#c4c4c4",
                            stroke: "#ffffff",
                            strokeWidth: 0.6,
                            outline: "none",
                            cursor: isAvailable ? "pointer" : "default",
                          },
                          pressed: {
                            fill: isAvailable ? "#005530" : "#c4c4c4",
                            stroke: "#ffffff",
                            strokeWidth: 0.6,
                            outline: "none",
                          },
                        }}
                      />
                    )
                  })
                }
              </Geographies>
            </ComposableMap>

            <div className='territory-map__legend'>
              <div className='legend-item'>
                <span className='legend-swatch legend-swatch--available' />
                Available territories
              </div>
              <div className='legend-item'>
                <span className='legend-swatch legend-swatch--unavailable' />
                Currently unavailable
              </div>
            </div>
          </div>

          <div className='territory-map__details animate-fade-up'>
            <div className='territory-map__card'>
              <p className='details-label'>Selected Territory</p>
              {selectedState ? (
                <>
                  <h3>{selectedState.name}</h3>
                  <p>
                    Let&apos;s talk through open markets, protected territory, and
                    your launch timeline.
                  </p>
                  <button className='btn-primary' onClick={scrollToBooking}>
                    Get Started in {selectedState.code}
                  </button>
                </>
              ) : (
                <>
                  <h3>Select an available state</h3>
                  <p>
                    Click any green state to check availability and connect with the
                    Cordova franchise team.
                  </p>
                </>
              )}
            </div>

            <div className='territory-map__available'>
              <p className='details-label'>Current Open Markets</p>
              <div className='territory-map__select-wrapper'>
                <select
                  value={selectedState?.code || ""}
                  onChange={(event) => {
                    const selectedCode = event.target.value
                    const state =
                      AVAILABLE_STATES.find((item) => item.code === selectedCode) ||
                      null
                    setSelectedState(state)
                  }}
                >
                  <option value=''>Select an available state</option>
                  {AVAILABLE_STATES.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TerritoryMap

